import { useCallback, useEffect, useMemo, useState } from "react";
import { workDayDateAt } from "@shyne/shared";
import { Link } from "react-router";
import MainLayout from "@/components/layouts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, ClipboardList, Copy, CopyPlus, Plus, Trash2 } from "lucide-react";
import { ServicesRepository } from "@/repositories/services.repository";
import { IndividualsRepository } from "@/repositories/individuals.repository";
import { DailyWorkRateProfileRepository } from "@/repositories/daily-work-rate-profile.repository";
import { useAppSelector } from "@/lib/store";
import { DailyWorkJoin } from "@/repositories/daily-work.repository";
import { listDailyWorkForVendor } from "./daily-work.api";
import {
  DailyWorkScheduleRepository,
  DailyWorkScheduleRowJoin,
  SaveScheduleRowInput,
} from "@/repositories/daily-work-schedule.repository";
import type { Service } from "@shyne/shared/api";
import { day_of_week } from "@/models/daily-work.model";
import { DailyWorkScheduleSyncResult } from "@/models/daily-work-schedule.model";
import {
  dayOfWeekLabels,
  indexToDay,
  monthlyOccurrenceLabels,
  orderedWeekdays,
} from "@/lib/daily-work/master-schedule";
import MasterScheduleRowDialog, { AssignmentOption } from "./components/MasterScheduleRowDialog";
import CreateDailyWorkModal from "./components/CreateDailyWorkModal";

function formatCurrency(amount: number | null | undefined) {
  if (amount == null) return "No rate";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

function describeRowSave(payload: SaveScheduleRowInput, savedCount: number): string {
  if (payload.id) return "Schedule row updated";
  if (savedCount === 1) return "Schedule row created";
  return `${savedCount} schedule rows created`;
}

function describeSync(sync: DailyWorkScheduleSyncResult): string {
  if (sync.frozen) return "contract is paused — no days generated until it is active again";
  const parts: string[] = [];
  if (sync.inserted > 0) parts.push(`${sync.inserted} day${sync.inserted === 1 ? "" : "s"} added`);
  if (sync.updated > 0) parts.push(`${sync.updated} updated`);
  if (sync.deleted > 0) parts.push(`${sync.deleted} removed`);
  if (sync.kept_locked > 0) {
    parts.push(`${sync.kept_locked} accepted/paid day${sync.kept_locked === 1 ? "" : "s"} left in place`);
  }
  if (sync.kept_detached > 0) {
    parts.push(`${sync.kept_detached} hand-edited day${sync.kept_detached === 1 ? "" : "s"} left as-is`);
  }
  return parts.length > 0 ? parts.join(", ") : "live schedule already up to date";
}

export default function MasterSchedulePage() {
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState<DailyWorkJoin[]>([]);
  const [rows, setRows] = useState<DailyWorkScheduleRowJoin[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [individuals, setIndividuals] = useState<AssignmentOption[]>([]);
  const [subs, setSubs] = useState<AssignmentOption[]>([]);
  const [rateMap, setRateMap] = useState<Record<string, number>>({});
  const { activeOrgId } = useAppSelector((state) => state.organization);
  const [contractFilter, setContractFilter] = useState<string>("all");
  const [rowDialogOpen, setRowDialogOpen] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyTargetId, setCopyTargetId] = useState<string>("");
  const [copying, setCopying] = useState(false);
  const [editingRow, setEditingRow] = useState<DailyWorkScheduleRowJoin | null>(null);
  const [templateRow, setTemplateRow] = useState<DailyWorkScheduleRowJoin | null>(null);
  const [defaultWeekday, setDefaultWeekday] = useState<day_of_week>("monday");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Staffing pickers need individuals.view, which not every daily_work role
      // has; the board itself must still render, so that load is non-fatal.
      const assignablePromise = IndividualsRepository.listAssignableResources().catch((error: any) => {
        toast.error(error.message || "Failed to load individuals and companies for staffing");
        return { individuals: [], subs: [] };
      });
      const [dailyWorkRes, scheduleRows, servicesRes, rates, assignable] = await Promise.all([
        listDailyWorkForVendor(1, 500, {}, "created_at", "desc"),
        DailyWorkScheduleRepository.listForVendor(),
        ServicesRepository.getParentServices(),
        activeOrgId ? DailyWorkRateProfileRepository.rateMapForPayer(activeOrgId) : Promise.resolve({}),
        assignablePromise,
      ]);

      const todayIso = workDayDateAt(new Date());
      const usableContracts = dailyWorkRes.data.filter(
        (item) => item.status !== "cancelled" && (!item.end_date || item.end_date >= todayIso)
      );
      setContracts(usableContracts);
      setRows(scheduleRows);
      setServices(servicesRes.data ?? []);
      setIndividuals(assignable.individuals);
      setSubs(assignable.subs);
      setRateMap(rates);
    } catch (error: any) {
      toast.error(error.message || "Failed to load master schedule");
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (contractFilter !== "all" && !contracts.some((contract) => contract.id === contractFilter)) {
      setContractFilter("all");
    }
  }, [contracts, contractFilter]);

  const contractMap = useMemo(() => new Map(contracts.map((contract) => [contract.id, contract])), [contracts]);

  const visibleRows = useMemo(() => {
    const contractIds = new Set(contracts.map((contract) => contract.id));
    return rows
      .filter((row) => contractIds.has(row.daily_work))
      .filter((row) => contractFilter === "all" || row.daily_work === contractFilter);
  }, [rows, contracts, contractFilter]);

  const rowsByWeekday = useMemo(() => {
    const grouped = new Map<day_of_week, DailyWorkScheduleRowJoin[]>();
    for (const day of orderedWeekdays) grouped.set(day, []);
    for (const row of visibleRows) {
      grouped.get(indexToDay[row.weekday])?.push(row);
    }
    for (const day of orderedWeekdays) {
      grouped.get(day)?.sort((a, b) => {
        const aName = a.daily_work_data?.property?.name ?? "";
        const bName = b.daily_work_data?.property?.name ?? "";
        return a.position - b.position || aName.localeCompare(bName) || a.created_at.localeCompare(b.created_at);
      });
    }
    return grouped;
  }, [visibleRows]);

  const visibleContracts = useMemo(
    () => (contractFilter === "all" ? contracts : contracts.filter((item) => item.id === contractFilter)),
    [contracts, contractFilter]
  );
  const hasOpenEndedContract = useMemo(
    () => visibleContracts.some((contract) => !contract.end_date),
    [visibleContracts]
  );

  const handleSaveRow = async (payload: SaveScheduleRowInput): Promise<boolean> => {
    try {
      const { rows, sync } = await DailyWorkScheduleRepository.saveRow(payload);
      toast.success(`${describeRowSave(payload, rows.length)} — ${describeSync(sync)}`);
      await loadData();
      return true;
    } catch (error: any) {
      toast.error(error?.message || "Failed to save schedule row");
      return false;
    }
  };

  const handleDeleteRow = async (row: DailyWorkScheduleRowJoin) => {
    const propertyName = row.daily_work_data?.property?.name ?? "this property";
    if (!window.confirm(`Delete the recurring ${propertyName} row on ${dayOfWeekLabels[indexToDay[row.weekday]]}?`)) {
      return;
    }

    try {
      const sync = await DailyWorkScheduleRepository.archiveRow(row.id);
      toast.success(`Schedule row deleted — ${describeSync(sync)}`);
      await loadData();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete row");
    }
  };

  const handleCopySchedule = async () => {
    if (contractFilter === "all" || !copyTargetId) return;

    setCopying(true);
    try {
      const copied = await DailyWorkScheduleRepository.copyScheduleToContract(contractFilter, copyTargetId);
      const targetName = contractMap.get(copyTargetId)?.property.name ?? "the selected contract";
      toast.success(`Copied ${copied} schedule row${copied === 1 ? "" : "s"} to ${targetName}`);
      setCopyDialogOpen(false);
      setCopyTargetId("");
      // Land the user on the copied schedule so the result is visible immediately.
      setContractFilter(copyTargetId);
      await loadData();
    } catch (error: any) {
      toast.error(error.message || "Failed to copy schedule");
    } finally {
      setCopying(false);
    }
  };

  const openCreateRow = (weekday: day_of_week) => {
    setEditingRow(null);
    setTemplateRow(null);
    setDefaultWeekday(weekday);
    setRowDialogOpen(true);
  };

  return (
    <MainLayout
      breadcrumb={[{ name: "Daily Work", href: "#" }]}
      pageHeader={{
        title: "Master Schedule",
        subtitle: "Build the recurring weekly staffing plan that generates Daily Work for individuals and subs.",
      }}
    >
      <div className="container mx-auto space-y-5">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-1 items-center">
            <Select value={contractFilter} onValueChange={setContractFilter}>
              <SelectTrigger className="w-full bg-background md:w-[360px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All property contracts</SelectItem>
                {contracts.map((contract) => (
                  <SelectItem key={contract.id} value={contract.id}>
                    {contract.property.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-2 xl:justify-end">
            {hasOpenEndedContract && (
              <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-800">
                Open-ended contracts sync the next 60 days
              </Badge>
            )}
            {contractFilter !== "all" && (
              <Button variant="outline" onClick={() => setCopyDialogOpen(true)}>
                <CopyPlus className="w-4 h-4 mr-2" />
                Copy Schedule To…
              </Button>
            )}
            <Button variant="outline" onClick={() => setContractModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Property Contract
            </Button>
            <Button variant="outline" asChild>
              <Link to="/dailywork/contracts">
                <ClipboardList className="w-4 h-4 mr-2" />
                Contracts
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/dailywork/calendar">
                <CalendarDays className="w-4 h-4 mr-2" />
                Calendar
              </Link>
            </Button>
          </div>
        </div>

        {loading ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">Loading master schedule…</CardContent>
          </Card>
        ) : contracts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <p className="text-muted-foreground">
                Create a property contract first, then add recurring schedule rows under it.
              </p>
              <Button onClick={() => setContractModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Property Contract
              </Button>
            </CardContent>
          </Card>
        ) : (
          <section className="space-y-3">
            <div>
              <h2 className="text-lg font-semibold">Weekly Board</h2>
              <p className="text-sm text-muted-foreground">
                Scroll horizontally to review the full week without compressing the schedule.
              </p>
            </div>

            <div className="overflow-x-auto pb-3">
              <div className="grid min-w-[2100px] grid-cols-7 gap-4">
                {orderedWeekdays.map((weekday) => {
                  const dayRows = rowsByWeekday.get(weekday) ?? [];

                  return (
                    <Card key={weekday} className="min-h-[420px] border-muted/70 shadow-sm">
                      <CardHeader className="space-y-3 pb-4">
                        <div className="flex items-center justify-between gap-3">
                          <CardTitle>{dayOfWeekLabels[weekday]}</CardTitle>
                          <Badge variant="secondary">{dayRows.length}</Badge>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => openCreateRow(weekday)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Row
                        </Button>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {dayRows.length === 0 ? (
                          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                            No recurring rows yet.
                          </div>
                        ) : (
                          dayRows.map((row) => {
                            const propertyName = row.daily_work_data?.property?.name ?? "Unknown property";
                            const contractStatus = contractMap.get(row.daily_work)?.status;

                            return (
                              <div key={row.id} className="rounded-xl border bg-background p-4 space-y-3">
                                <div className="space-y-1.5">
                                  <div className="flex items-start justify-between gap-2">
                                    <p className="font-medium leading-tight">{propertyName}</p>
                                    {contractStatus === "paused" && <Badge variant="secondary">Paused</Badge>}
                                  </div>
                                  <div className="space-y-1">
                                    <p className="text-sm font-medium">{row.service_data?.name ?? "Unknown service"}</p>
                                    {(row.duration_label || row.max_hours != null) && (
                                      <p className="text-xs text-muted-foreground">
                                        {row.duration_label || `${row.max_hours} hrs`}
                                      </p>
                                    )}
                                    {row.instructions && (
                                      <p className="text-xs leading-relaxed text-muted-foreground">{row.instructions}</p>
                                    )}
                                  </div>
                                </div>

                                <Separator />

                                <div className="space-y-2">
                                  <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                                    Staffing
                                  </p>
                                  {row.assignments.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">Generates an unassigned slot</p>
                                  ) : (
                                    row.assignments.map((assignment) => {
                                      const assigneeName =
                                        assignment.assignment_kind === "company"
                                          ? assignment.assigned_org_data?.display_name
                                          : assignment.assigned_user_data?.name;
                                      const profileRate =
                                        assignment.assignment_kind === "company"
                                          ? rateMap[`org:${assignment.assigned_org}:${row.service}`] ?? null
                                          : rateMap[`user:${assignment.assigned_user}:${row.service}`] ?? null;
                                      const effectiveRate = assignment.rate_override ?? profileRate;

                                      return (
                                        <div key={assignment.id} className="rounded-lg bg-muted/50 px-3 py-2 text-xs">
                                          <div className="font-medium">
                                            {assigneeName || "Unassigned"}
                                            {assignment.quantity > 1 ? ` — ${assignment.quantity} people` : ""}
                                          </div>
                                          <div className="mt-0.5 text-muted-foreground">
                                            {formatCurrency(effectiveRate)}
                                            {assignment.rate_override != null ? " (override)" : ""}
                                          </div>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>

                                <div className="space-y-2">
                                  <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Tasks</p>
                                  <p className="text-sm">{row.weekly_tasks.length} weekly task(s)</p>
                                  {row.monthly_rules.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {row.monthly_rules.map((rule) => (
                                        <Badge key={rule.id} variant="outline" className="text-[10px]">
                                          {monthlyOccurrenceLabels[rule.occurrence]}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                <div className="flex gap-2 pt-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => {
                                      setEditingRow(row);
                                      setDefaultWeekday(indexToDay[row.weekday]);
                                      setRowDialogOpen(true);
                                    }}
                                  >
                                    Edit
                                  </Button>
                                  <Button variant="outline" size="sm" asChild className="flex-1">
                                    <Link to={`/dailywork/${row.daily_work}`}>Exceptions</Link>
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Duplicate row"
                                    onClick={() => {
                                      setEditingRow(null);
                                      setTemplateRow(row);
                                      setDefaultWeekday(indexToDay[row.weekday]);
                                      setRowDialogOpen(true);
                                    }}
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" title="Delete row" onClick={() => handleDeleteRow(row)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <MasterScheduleRowDialog
          open={rowDialogOpen}
          onOpenChange={(open) => {
            setRowDialogOpen(open);
            if (!open) {
              setEditingRow(null);
              setTemplateRow(null);
            }
          }}
          contracts={contracts}
          services={services}
          individuals={individuals}
          subs={subs}
          rateMap={rateMap}
          row={editingRow}
          templateRow={templateRow}
          defaultWeekday={defaultWeekday}
          defaultDailyWorkId={contractFilter !== "all" ? contractFilter : null}
          onSave={handleSaveRow}
        />

        <CreateDailyWorkModal
          open={contractModalOpen}
          onOpenChange={setContractModalOpen}
          onSuccess={async () => {
            toast.success("Property contract created");
            setContractModalOpen(false);
            await loadData();
          }}
        />

        <Dialog
          open={copyDialogOpen}
          onOpenChange={(open) => {
            setCopyDialogOpen(open);
            if (!open) setCopyTargetId("");
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Copy Schedule To Another Contract</DialogTitle>
              <DialogDescription>
                Copies every recurring row from{" "}
                <span className="font-medium">
                  {contractFilter !== "all" ? contractMap.get(contractFilter)?.property.name : ""}
                </span>{" "}
                and generates the upcoming schedule for the target contract. You will land on the copied schedule.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Select value={copyTargetId} onValueChange={setCopyTargetId}>
                <SelectTrigger className="w-full bg-background">
                  <SelectValue placeholder="Choose target contract" />
                </SelectTrigger>
                <SelectContent>
                  {contracts
                    .filter((contract) => contract.id !== contractFilter)
                    .map((contract) => (
                      <SelectItem key={contract.id} value={contract.id}>
                        {contract.property.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Need a contract for a new property? Close this and use Create Property Contract first.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCopyDialogOpen(false)} disabled={copying}>
                Cancel
              </Button>
              <Button onClick={handleCopySchedule} disabled={copying || !copyTargetId}>
                {copying ? "Copying…" : "Copy Schedule"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
